import type { BulkPropertyRow, ReuseCandidate } from "../types";
import type { AcquirePolicy } from "./acquirePolicy";
import { isAllMattersField } from "./checkboxFields";
import {
  isUsableByWindow,
  othersReusablePictures,
  pictureTypeOfField,
  zenbujikoFields,
} from "./reuseAvailability";
import { findCandidates } from "./reuseCandidates";

/** セルに出す印。取得済みは自社取得済み（照合結果の acquiredPictures）がある列 */
export interface CellReuseMarks {
  acquired: boolean;
  reusable: boolean;
}

const NO_MARKS: CellReuseMarks = { acquired: false, reusable: false };

export interface SplitCandidates {
  acquired: ReuseCandidate[];
  reusable: ReuseCandidate[];
}

const computeSplit = (
  field: string,
  row: BulkPropertyRow
): SplitCandidates | null => {
  const isAllMatters = isAllMattersField(field);
  const pictureType = pictureTypeOfField(field);
  if (pictureType == null) {
    return null;
  }
  // 全部事項はその列が候補の目録構成に含まれる候補だけを対象にする
  const coversField = (candidate: ReuseCandidate): boolean =>
    !isAllMatters || zenbujikoFields(candidate).includes(field);

  const acquired = findCandidates(row.acquiredPictures, pictureType).filter(
    coversField
  );
  const reusable = findCandidates(
    othersReusablePictures(row),
    pictureType
  ).filter(coversField);
  return { acquired, reusable };
};

// 行オブジェクトは照合結果のメモ化で参照が安定しているため、行を鍵にして列ごとの分割結果を保持する。
// 印・点の状態・ツールチップ・不可判定が同じセルで何度も呼ぶが、走査は行×列につき1回で済む
const splitCache = new WeakMap<
  BulkPropertyRow,
  Map<string, SplitCandidates | null>
>();

/** その列の候補を取得済み・他ユーザーのリユースに分けて返す（種別で取得できない列は null） */
export const splitCandidates = (
  field: string,
  row: BulkPropertyRow
): SplitCandidates | null => {
  let byField = splitCache.get(row);
  if (byField == null) {
    byField = new Map();
    splitCache.set(row, byField);
  }
  let split = byField.get(field);
  if (split === undefined) {
    split = computeSplit(field, row);
    byField.set(field, split);
  }
  return split;
};

/**
 * そのセルに候補があるかを取得済み・リユースの2軸で返す。
 * 取得条件・経過日数には依存させない（候補があるという事実だけを示し、それが今の
 * 条件で使えるかは isReuseUsableNow・チェックの活性・ツールチップが受け持つ）
 */
export const getCellReuseMarks = (
  field: string,
  row: BulkPropertyRow
): CellReuseMarks => {
  const split = splitCandidates(field, row);
  if (split == null) {
    return NO_MARKS;
  }
  return {
    acquired: split.acquired.length > 0,
    reusable: split.reusable.length > 0,
  };
};

/**
 * リユース候補が今の取得条件・経過日数で使えるか。
 * リユースを使わない条件では常に false
 */
export const isReuseUsableNow = (
  field: string,
  row: BulkPropertyRow,
  policy: AcquirePolicy,
  pastDays: number,
  now: Date
): boolean => {
  if (!policy.steps.includes("reuse")) {
    return false;
  }
  const split = splitCandidates(field, row);
  return (
    split?.reusable.some((candidate) =>
      isUsableByWindow(candidate, policy, pastDays, now)
    ) ?? false
  );
};

/**
 * 未取得のみを取得する指定で、そのセルを取得済み扱いにするか。
 * 全部事項は目録構成の単位で取得しており列ごとに取り直せないため、
 * 自身の取得済みが1つでもあれば4列まとめて取得済み扱いにする（列単位だと
 * 「共担・信託は押せないが現在は押せる」のような半端な状態を見せることになる）
 */
export const isAcquiredForExclusion = (
  field: string,
  row: BulkPropertyRow
): boolean =>
  isAllMattersField(field)
    ? acquiredZenbujikoFields(row).size > 0
    : getCellReuseMarks(field, row).acquired;

/** 全部事項のうち、その行で自社取得済みの目録構成に現れる列 */
export const acquiredZenbujikoFields = (row: BulkPropertyRow): Set<string> =>
  new Set(
    findCandidates(row.acquiredPictures, "ZENBUJIKO").flatMap(zenbujikoFields)
  );
